/**
 * ContentMgmtDataConsistency.js (Iter 55 · Feb 2026)
 * -----------------------------------------------------
 * Admin diagnostic page. Groups every non-canonical value in
 * users / content_library / syllabus_submissions and offers a
 * one-click "Normalize" per variant. Backed by:
 *   GET  /api/admin/data-consistency/scan
 *   POST /api/admin/data-consistency/normalize
 *
 * Design intent:
 *   - Green shield when everything is canonical (should be the norm).
 *   - Red banner + collapsible cards when drift is detected.
 *   - Every normalize is one variant at a time (small blast radius).
 *   - The action is idempotent + audit-logged server side, so admins
 *     can run it liberally during maintenance windows.
 */
import { useEffect, useState, useCallback } from 'react';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import { apiGet, apiPost } from '../../lib/api';
import {
  ShieldCheck, ShieldAlert, RefreshCw, ChevronDown, ChevronRight,
  ArrowRight, Loader2, AlertTriangle, Database,
} from 'lucide-react';

const COLL_META = {
  users: { label: 'Users', hint: 'Student/parent onboarding profiles.' },
  content_library: { label: 'Content Library', hint: 'Uploaded infographics / cinegraphics.' },
  syllabus_submissions: { label: 'Syllabus Submissions', hint: 'Admin-approved PDF chapters.' },
};

const FIELD_META = {
  board: { label: 'Board', hint: 'Should always be a canonical key like "cbse", "icse".' },
  class_level: { label: 'Class Level', hint: 'Should be a plain integer string ("8", "10"). Roman numerals & "Class 8" get normalized.' },
  status: { label: 'Status', hint: 'One of: approved, active, pending_review, rejected.' },
};

export default function ContentMgmtDataConsistency() {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState({});
  const [normalizing, setNormalizing] = useState(null); // key: `${coll}|${field}|${from}`

  const scan = useCallback(async () => {
    setLoading(true);
    try {
      const d = await apiGet('/admin/data-consistency/scan');
      setReport(d);
    } catch (e) {
      toast.error(e?.message || 'Scan failed');
    }
    setLoading(false);
  }, []);

  useEffect(() => { scan(); }, [scan]);

  const runNormalize = async (collection, field, from_value, to_value) => {
    const key = `${collection}|${field}|${from_value}`;
    setNormalizing(key);
    try {
      const res = await apiPost('/admin/data-consistency/normalize',
        { collection, field, from_value, to_value });
      toast.success(`Normalized ${res.modified} row${res.modified === 1 ? '' : 's'} · ${from_value} → ${to_value}`);
      await scan();
    } catch (e) {
      toast.error(e?.message || 'Normalize failed');
    }
    setNormalizing(null);
  };

  const toggle = (k) => setExpanded(prev => ({ ...prev, [k]: !prev[k] }));

  return (
    <div className="space-y-4" data-testid="data-consistency-root">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Database className="w-6 h-6 text-indigo-600" />
        <div className="flex-1">
          <h2 className="text-lg font-heading text-slate-800">Data Consistency Check</h2>
          <p className="text-xs text-slate-500 font-body">
            Scans <strong>users</strong>, <strong>content_library</strong>, and <strong>syllabus_submissions</strong> for
            non-canonical field values that cause silent read-mismatches (root cause of the Feb-2026 &quot;no content in Gallery&quot; bug).
          </p>
        </div>
        <Button onClick={scan} disabled={loading} data-testid="dc-rescan-btn"
          variant="outline" className="rounded-full">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
          <span className="ml-1.5">Rescan</span>
        </Button>
      </div>

      {/* Overall health banner */}
      {report && (
        <Card className={`p-4 rounded-2xl border-2 ${
          report.healthy
            ? 'border-emerald-200 bg-gradient-to-br from-emerald-50 to-white'
            : 'border-rose-200 bg-gradient-to-br from-rose-50 to-white'
        }`}
          data-testid="dc-health-banner">
          <div className="flex items-start gap-3">
            {report.healthy
              ? <ShieldCheck className="w-8 h-8 text-emerald-600 shrink-0" />
              : <ShieldAlert className="w-8 h-8 text-rose-600 shrink-0" />}
            <div className="flex-1 min-w-0">
              <p className="font-heading text-slate-800">
                {report.healthy
                  ? 'All values canonical — no drift detected.'
                  : `${report.total_issues} row${report.total_issues === 1 ? '' : 's'} across ${countAffectedFields(report.report)} field${countAffectedFields(report.report) === 1 ? '' : 's'} need normalization.`}
              </p>
              <p className="text-xs text-slate-500 font-body mt-0.5">
                Last scanned: {report.generated_at ? new Date(report.generated_at).toLocaleString() : '—'}
              </p>
              {!report.healthy && (
                <p className="text-[11px] text-slate-500 font-body mt-2">
                  <AlertTriangle className="w-3 h-3 inline mr-1 text-amber-600" />
                  Each normalize is idempotent + audit-logged and only touches the exact variant you click. Safe to run during business hours.
                </p>
              )}
            </div>
          </div>
        </Card>
      )}

      {/* Per-collection sections */}
      {report && !report.healthy && Object.entries(report.report).map(([coll, fields]) => {
        const unhealthy = Object.entries(fields).filter(([, d]) => !d.healthy);
        if (unhealthy.length === 0) return null;
        return (
          <Card key={coll} className="p-4 rounded-2xl border-slate-100" data-testid={`dc-coll-${coll}`}>
            <div className="flex items-center gap-2 mb-3">
              <h3 className="font-heading text-slate-800 text-sm">{COLL_META[coll]?.label || coll}</h3>
              <span className="text-[10px] text-slate-400 font-body">{COLL_META[coll]?.hint}</span>
            </div>
            <div className="space-y-2">
              {unhealthy.map(([field, data]) => {
                const key = `${coll}|${field}`;
                const isOpen = expanded[key] !== false; // default open
                return (
                  <div key={field} className="border border-slate-100 rounded-xl overflow-hidden" data-testid={`dc-field-${coll}-${field}`}>
                    <button onClick={() => toggle(key)}
                      className="w-full flex items-center gap-2 px-3 py-2.5 bg-slate-50/60 hover:bg-slate-50 text-left">
                      {isOpen ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
                      <span className="font-heading text-xs text-slate-700">{FIELD_META[field]?.label || field}</span>
                      <span className="text-[10px] text-slate-500">{FIELD_META[field]?.hint}</span>
                      <span className="ml-auto inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-heading bg-rose-100 text-rose-700">
                        <AlertTriangle className="w-3 h-3" /> {data.total_variants} rows
                      </span>
                    </button>
                    {isOpen && (
                      <div className="px-3 py-3 space-y-3">
                        {data.groups.filter(g => g.variants.length > 0).map(g => (
                          <div key={g.canonical} className="rounded-lg bg-slate-50/50 p-3">
                            <div className="flex items-center gap-2 mb-2">
                              <span className="text-[10px] uppercase tracking-wider text-slate-500 font-heading">Canonical</span>
                              <code className="text-xs font-mono px-1.5 py-0.5 bg-emerald-100 text-emerald-800 rounded">
                                {g.canonical || '(empty)'}
                              </code>
                              <span className="text-[10px] text-slate-400">{g.canonical_count} rows already canonical</span>
                            </div>
                            <div className="space-y-1.5">
                              {g.variants.map(v => {
                                const nkey = `${coll}|${field}|${v.value}`;
                                const busy = normalizing === nkey;
                                return (
                                  <div key={v.value}
                                    className="flex items-center gap-2 px-2 py-1.5 bg-white rounded-md border border-slate-100"
                                    data-testid={`dc-variant-${coll}-${field}-${v.value}`}>
                                    <code className="text-xs font-mono text-rose-700 truncate flex-1" title={v.value}>
                                      {v.value === '' ? '(empty string)' : v.value}
                                    </code>
                                    <span className="text-[10px] text-slate-500 shrink-0">{v.count} rows</span>
                                    <ArrowRight className="w-3 h-3 text-slate-300 shrink-0" />
                                    <code className="text-xs font-mono text-emerald-700 shrink-0">{g.canonical || '(empty)'}</code>
                                    <Button size="sm"
                                      disabled={busy || v.value === ''}
                                      onClick={() => runNormalize(coll, field, v.value, g.canonical)}
                                      data-testid={`dc-normalize-btn-${coll}-${field}-${v.value}`}
                                      className="h-7 px-2 text-[10px] bg-indigo-600 hover:bg-indigo-700 text-white rounded-full disabled:opacity-40">
                                      {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Normalize'}
                                    </Button>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </Card>
        );
      })}
    </div>
  );
}

function countAffectedFields(report) {
  let n = 0;
  for (const fields of Object.values(report || {})) {
    for (const d of Object.values(fields)) {
      if (!d.healthy) n++;
    }
  }
  return n;
}
